import {Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {MatSnackBar} from "@angular/material/snack-bar";
import {combineLatest, EMPTY, Observable, of, switchMap} from "rxjs";
import {distinctUntilChanged, map, shareReplay, startWith, take, tap} from "rxjs/operators";
import {TranslocoService} from "@jsverse/transloco";
import {LinkGen} from "../../../../shared/constants/loren-routes.constants";
import {
    LorenMeteringContext,
    METERING_CONTEXT_SHOW_FIELD_MAP,
    METERING_CONTEXT_TRANSLATION_NAMES,
} from "../../enums/loren-metering-context.enum";
import {LorenMeteringType, LorenMeteringTypeTranslation,} from "../../interfaces/loren-metering-type";
import {LorenAsset,} from "../../interfaces/loren-asset.interface";
import {LorenPrivilegeEntityType} from "../../../loren-roles/interfaces/loren-role.interface";
import {
    LorenDetailsOutletService
} from "../../../../shared/services/loren-details-outlet-service/loren-details-outlet.service";
import {
    LorenAuthenticationService
} from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import {
    LorenAssetPropertiesService
} from "../../services/loren-asset-properties-service/loren-asset-properties.service";
import {LorenAssetTypeService} from "../../services/loren-asset-type-service/loren-asset-type.service";
import {LorenUtilsService} from "../../../../shared/services/loren-utils-service/loren-utils.service";
import {DeviceService} from "../../../device/device.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
    LocationValidator
} from "../../../../shared/modules/loren-asset-location-selection/components/asset-location-selection/asset-location-selection.component";
import {LorenMetering} from "../../interfaces/loren-metering.interface";
import {AssetService, CreateAssetDTO} from "../../../asset/asset.service";
import {
    AssetSensorDialogComponent,
    SensorFormGroup,
    SensorFormGroupValidators
} from "../asset-sensor-dialog/asset-sensor-dialog.component";
import {ExtraValidator} from "../../../../common/forms/form.utils";
import {toLatLng, toLorenLocation} from "../../interfaces/loren-location.interface";
import {LatLngLiteral} from "leaflet";
import {DialogService} from "../../../../common/dialog/dialog.service";

export interface DeviceItem {
    id: number;
    deviceName: string;
    imei: string;
    sensors: {
        translationKey: string;
        sensorId: number;
        disabled: boolean;
        mac?: string;
        hasMeteringContext: boolean;
    }[],
}

@Component({
    templateUrl: "./asset-form.component.html",
    styleUrls: ["./asset-form.component.css"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class AssetFormComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly dialogService = inject(DialogService)
    private readonly fb = inject(FormBuilder);
    private readonly snackBar = inject(MatSnackBar);
    private readonly translateService = inject(TranslocoService);

    private readonly assetService = inject(AssetService);
    private readonly deviceService = inject(DeviceService);

    METERING_CONTEXT_TRANSLATION_NAMES = METERING_CONTEXT_TRANSLATION_NAMES;

    // Legacy injectables
    protected detailsOutletService = inject(LorenDetailsOutletService);
    private authenticationService = inject(LorenAuthenticationService);
    protected propertiesService = inject(LorenAssetPropertiesService);
    private lorenAssetTypeService = inject(LorenAssetTypeService);

    protected readonly form = this.fb.group({
        basic: this.fb.group({
            name: this.fb.nonNullable.control("", [Validators.required]),
            type: this.fb.control<number | null>(null, [Validators.required]),
            groups: this.fb.nonNullable.control<number[]>([]),
            description: this.fb.nonNullable.control("", []),
            defaultLocation: this.fb.control<LatLngLiteral | null>(null, [LocationValidator]),
        }),
        properties: this.fb.record<FormControl<string>>({}),
        sensors: this.fb.array<FormGroup<SensorFormGroup>>([], [ExtraValidator.minArrayLength(1)]),
    })

    // New observables
    private readonly assetId$ = this.activatedRoute.params.pipe(
        map(params => {
            if (params.id) {
                return +params.id;
            }

            return undefined;
        }),
        distinctUntilChanged(),
    );

    asset$ = this.assetId$.pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((id) => {
            if (id === undefined) {
                return of(undefined);
            }

            return this.assetService.get(id);
        }),
        take(1),
        shareReplay(1),
    );

    propertyLabelMap$ = this.propertiesService.query().pipe(
        takeUntilDestroyed(this.destroyRef),
        map(response => response.items),
        map(properties =>
            new Map(
                properties.map(property => [
                    property.id,
                    `properties.${property.name}`
                ] as [number, string])
            )
        ),
        shareReplay({bufferSize: 1, refCount: true})
    );

    protected get basic() {
        return this.form.controls.basic;
    }

    protected get properties() {
        return this.form.controls.properties;
    }

    protected get sensors() {
        return this.form.controls.sensors;
    }

    selectedImage = signal<string | null>(null)
    readonly privileges = LorenPrivilegeEntityType;

    assetTypes$ = this.lorenAssetTypeService.query().pipe(
        map(x => x.items),
        shareReplay(1),
    );

    private readonly selectedType$ = this.basic.controls.type.valueChanges.pipe(
        shareReplay(1),
        startWith(this.basic.controls.type.value),
    );

    private readonly sensors$ = this.sensors.valueChanges.pipe(
        shareReplay(1),
        startWith(this.sensors.value)
    );

    selectedSensorIds$ = this.sensors$.pipe(
        map(sensor => sensor.map(x => x.sensorId)),
    )

    autocompleteItems$: Observable<DeviceItem[]> = combineLatest([
        this.deviceService.all,
        this.selectedSensorIds$,
    ]).pipe(
        map(([devices, selected]) => devices.map(device => {
            // We are showing sensor who are either:
            // - Transmitting data
            // - Previously selected (for edit form)
            const sensors = device.sensors.filter(sensor => {
                return sensor.isTransmittingData || selected.includes(sensor.id)
            }).map(sensor => ({
                translationKey: LorenMeteringTypeTranslation.get(sensor.type),
                sensorId: sensor.id,
                disabled: sensor.occupied || selected.includes(sensor.id),
                mac: sensor.mac,
            }))

            return {
                id: device.id,
                deviceName: device.name,
                imei: device.physicalAddress,
                sensors,
            } as DeviceItem;
        }))
    )

    sensorsDrag$ = combineLatest([
        this.sensors$,
        this.deviceService.all,
        this.asset$,
    ]).pipe(
        map(([sensors, devices, asset]) => sensors.map(sensorEntry => {
            const device = devices.find(device => device.sensors.map(x => x.id).includes(sensorEntry.sensorId));
            const sensor = device?.sensors.find(sensors => sensors.id === sensorEntry.sensorId);
            const metering = asset?.metering?.find(x => x.id === sensorEntry.meteringId);

            const sensorType = sensor?.type ?? metering?.type;

            return {
                sensorId: sensor?.id,
                deviceName: device?.name,
                sensorTranslationKey: sensorType ? LorenMeteringTypeTranslation.get(sensorType) : undefined,
                meteringContext: sensorEntry.meteringContext,
                description: sensorEntry.description,
            } as const;
        }))
    )

    ngOnInit(): void {
        combineLatest([
            this.selectedType$,
            this.assetTypes$,
            this.asset$
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([selectedType, types, asset]) => {
                const type = types.find(x => x.id === selectedType);

                if (!type) {
                    return;
                }

                // Add all non-existent properties
                for (const propertyID of type.properties) {
                    if (!this.properties.contains(propertyID.toString())) {
                        const data = asset?.propertiesData.find(x => x.propertyTypeId === propertyID)

                        this.properties.addControl(propertyID.toString(), this.fb.nonNullable.control(data?.value ?? ''));
                    }
                }

                // Remove all unnecessary ones
                for (const key of Object.keys(this.properties.controls)) {
                    if (!type.properties.map(x => x.toString()).includes(key)) {
                        this.properties.removeControl(key);
                    }
                }
            });

        // Prefill form
        this.asset$.subscribe({
            next: (asset: LorenAsset | undefined) => {
                if (asset === undefined) {
                    this.basic.reset();
                    this.basic.controls.type.enable();
                    this.sensors.clear();
                    return;
                }

                this.basic.patchValue({
                    name: asset.name,
                    type: asset.typeId,
                    description: asset.description,
                    groups: asset.belongsToGroups,
                    defaultLocation: toLatLng(asset.defaultLocation),
                });
                this.basic.controls.type.disable();

                for (const metering of asset.metering) {
                    const shouldHaveMeteringContext = METERING_CONTEXT_SHOW_FIELD_MAP.get(metering.type)

                    const group = this.fb.group<SensorFormGroup>({
                        meteringId: this.fb.control(metering.id),
                        sensorId: this.fb.control(metering.sensorId),
                        description: this.fb.nonNullable.control(metering.description, [Validators.required]),
                    }, {validators: SensorFormGroupValidators});

                    if (shouldHaveMeteringContext) {
                        const initial = this.getMeteringContextFromType(metering);
                        group.addControl('meteringContext', new FormControl(initial, [Validators.required]))
                    }

                    this.sensors.push(group);
                }
            }
        })
    }

    handleSubmitForm() {
        this.assetId$.pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap(id => {
                if (!this.form.valid) {
                    return EMPTY;
                }
                const form = this.form.getRawValue();
                const data: CreateAssetDTO = {
                    name: form.basic.name,
                    description: form.basic.description,
                    typeId: form.basic.type,
                    belongsToGroups: form.basic.groups,
                    propertiesData: Object.entries(form.properties).map(([id, value]) => ({
                        propertyTypeId: +id,
                        value,
                    })),
                    defaultLocation: toLorenLocation(form.basic.defaultLocation),
                    sensors: form.sensors.map(x => ({
                        meteringId: x.meteringId,
                        sensorId: x.sensorId,
                        meteringDescription: x.description,
                        meteringContext: x.meteringContext,
                    }))
                }

                if (id === undefined) {
                    return this.assetService.create(data).pipe(
                        switchMap(x => this.handlePhotoUpload(x)),
                        tap((asset) => {
                            this.showSuccessMessage();
                            this.onSaveSuccessful(asset);
                        })
                    )
                }

                return this.assetService.edit(id, data).pipe(
                    switchMap(x => this.handlePhotoUpload(x)),
                    tap(() => {
                        this.showSuccessMessage();
                        this.onSaveSuccessful();
                    })
                )
            }),
        ).subscribe({
            error: err => {
                const message = err?.error?.message ?? this.translateService.translate("operations.system_error");
                if (message) {
                    this.snackBar.open(message, null, {duration: 10_000});
                }
            }
        })
    }

    handlePhotoUpload(asset: LorenAsset) {
        if (this.selectedImage()) {
            return this.assetService.updateAssetImage(asset.id, this.selectedImage())
                .pipe(map(() => asset))
        }

        return of(asset);
    }

    handleDeleteSensor(index: number) {
        this.sensors.removeAt(index)
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(
            this.sensors.controls,
            event.previousIndex,
            event.currentIndex
        );
        this.sensors.updateValueAndValidity();
    }

    private showSuccessMessage() {
        this.snackBar.open(this.translateService.translate("operations.successful"), null, {duration: 3000});
        this.router.navigateByUrl(LinkGen.Assets());
    }

    private onSaveSuccessful(result: LorenAsset = null) {
        const dashboardPreferences =
            LorenUtilsService.getDashboardWidgetsPreferences(this.authenticationService.userId);
        if (dashboardPreferences && result) {
            dashboardPreferences.set(result.id, {
                show: true,
                ordinal: dashboardPreferences.size,
            });
            LorenUtilsService.setDashboardWidgetsPreferences(
                this.authenticationService.userId,
                dashboardPreferences
            );
        }
    }

    private getMeteringContextFromType(x: LorenMetering) {
        switch (x.type) {
            case LorenMeteringType.BINARY_SENSOR:
                return x.meteringContext || LorenMeteringContext.DOOR_WINDOW;
            case LorenMeteringType.DOOR_WINDOW_BINARY_SENSOR:
                return LorenMeteringContext.DOOR_WINDOW;
            case LorenMeteringType.IGNITION_BINARY_SENSOR:
                return LorenMeteringContext.VEHICLE_IGNITION;
            case LorenMeteringType.SMOKE_BINARY_SENSOR:
                return LorenMeteringContext.SMOKE;
            case LorenMeteringType.TILT_BINARY_SENSOR:
                return LorenMeteringContext.TILT;
            case LorenMeteringType.WATER_LEAK_BINARY_SENSOR:
                return LorenMeteringContext.WATER_LEAK;
            case LorenMeteringType.PIR_BINARY_SENSOR:
                return LorenMeteringContext.MOVEMENT;
            default:
                return undefined;
        }
    }

    protected editSensor(index: number) {
        const sensor = this.sensors.controls.at(index);
        this.dialogService.open(AssetSensorDialogComponent, {
            // Keep focus off the device autocomplete, it opens its panel on focus.
            autoFocus: 'dialog',
            data: {
                sensor: sensor,
                autocompleteItems$: this.autocompleteItems$,
            }
        }).afterClosed()
            .subscribe({
                next: sensorGroup => {
                    if (sensorGroup) {
                        this.sensors.setControl(index, sensorGroup);
                    }
                },
            })
    }

    protected addSensor() {
        this.dialogService.open(AssetSensorDialogComponent, {
            // Keep focus off the device autocomplete, it opens its panel on focus.
            autoFocus: 'dialog',
            data: {
                autocompleteItems$: this.autocompleteItems$,
            }
        }).afterClosed()
            .subscribe({
                next: sensorGroup => {
                    if (sensorGroup) {
                        this.sensors.push(sensorGroup);
                    }
                },
            })
    }
}
